// site planner templates: inputs -> guide values -> parts list from a manufacturer catalogue
const n = (v, d) => (v === '' || v == null || !Number.isFinite(+v) ? d : +v);
export const L = (t, de, en) => (String(t.locale || '').startsWith('de') ? de : en);

// nearest item of a role with DN >= target (or the largest available); optional dim / class / name filter
export function pick(items, role, o = {}) {
  let list = items.filter((x) => x.role === role && (!o.cls || !x.cls || x.cls === o.cls) && (!o.dim || String(x.dim).includes(o.dim)) && (!o.name || x.name.includes(o.name)));
  if (!list.length && (o.dim || o.name)) list = items.filter((x) => x.role === role);
  if (!list.length) return null;
  if (!o.dn) return list[0];
  const up = list.filter((x) => x.dn >= o.dn).sort((a, b) => a.dn - b.dn);
  return up[0] || [...list].sort((a, b) => b.dn - a.dn)[0];
}
const dnOf = (v) => n(v, 100);
const DNS = [50, 80, 100, 150, 200, 300].map((d) => [d, `DN ${d}`]);

const POND = {
  id: 'pond', icon: '🚒', name: (t) => L(t, 'Löschwasserteich / -behälter', 'Fire water pond / tank'),
  fields: [
    { k: 'q', type: 'sel', label: (t) => t.fpQ, def: 96, opts: [[48, '48 m³/h'], [96, '96 m³/h'], [192, '192 m³/h']] },
    { k: 't', label: (t) => t.fpT, def: 2 }, { k: 'v', label: (t) => t.fpV, def: '' }, { k: 'depth', label: (t) => t.fpDepth, def: 2.5 },
    { k: 'n', label: (t) => t.fpN, def: '' }, { k: 'len', label: (t) => t.fpLen, def: 8 }, { k: 'lift', label: (t) => t.fpLift, def: 3 },
    { k: 'load', type: 'sel', label: (t) => t.fpLoad, def: 'road', opts: (t) => [['road', t.fpLoadRoad], ['green', t.fpLoadGreen]] },
    { k: 'frost', type: 'chk', label: (t) => t.fpFrost, def: true }, { k: 'shaft', type: 'chk', label: (t) => t.fpShaft, def: true },
    { k: 'wall', type: 'chk', label: (t) => t.fpWall, def: true }, { k: 'feed', type: 'chk', label: (t) => t.fpFeed, def: false }, { k: 'level', type: 'chk', label: (t) => t.fpLevel, def: false },
  ],
  calc(p) {
    const Q = n(p.q, 96), T = n(p.t, 2), V = n(p.v, Q * T), depth = Math.max(1, n(p.depth, 2.5));
    const nSs = Math.max(1, Math.round(n(p.n, Math.ceil(Q / 96)))), qSs = Q / nSs, dn = qSs <= 48 ? 100 : qSs <= 96 ? 125 : 150;
    const len = n(p.len, 8), lift = n(p.lift, 3);
    return { Q, V, nSs, dn, len, area: V / Math.max(0.5, depth - 0.5), warn: [lift > 6 && 'fpWarnLift', len > 15 && 'fpWarnLen'].filter(Boolean) };
  },
  kpis: (t, c, f) => [[t.fpV, `${f(c.V)} m³`, 'v'], [t.fpArea, `${f(c.area)} m²`, 'area'], [t.fpN, c.nSs, 'n'], [t.fpDn, `DN ${c.dn}`, 'dn']],
  bom(t, p, c, I, add, other) {
    add(pick(I, p.frost !== false ? 'lw-saug-frost' : 'lw-saug', { dn: c.dn }), c.nSs);
    add(pick(I, 'lw-korb', { dn: c.dn }), c.nSs);
    other(t.fpPipe, `DN ${c.dn}`, 'PE 100', 'lfm', Math.ceil(c.nSs * c.len));
    add(pick(I, 'bend', { dn: c.dn }), c.nSs);
    if (p.wall !== false) add(pick(I, 'wall', { dn: c.dn }), c.nSs);
    if (p.shaft !== false) { add(pick(I, 'lw-schacht', { dn: c.dn }), c.nSs); add(pick(I, 'cover', { cls: p.load === 'green' ? 'B125' : 'D400' }), c.nSs); }
    add(pick(I, 'lw-bel', { dn: c.dn }), 1);
    if (p.feed) add(pick(I, 'lw-feed'), 1);
    if (p.level) add(pick(I, 'lw-level'), 1);
    add(pick(I, 'lw-sign'), c.nSs);
  },
};

const FLUSH = {
  id: 'flush', icon: '🚿', name: (t) => L(t, 'Spülhydrant / Hygienespülung', 'Flushing hydrant / hygiene flushing'),
  fields: [
    { k: 'cnt', label: (t) => L(t, 'Anzahl Spülpunkte', 'Number of flushing points'), def: 1 },
    { k: 'dn', type: 'sel', label: (t) => L(t, 'Leitung DN', 'Main DN'), def: 100, opts: DNS.filter(([d]) => d >= 80 && d <= 150) },
    { k: 'kind', type: 'sel', label: (t) => L(t, 'Bauart', 'Type'), def: 'uf', opts: (t) => [['uf', L(t, 'Unterflur (SPÜL-FLEX UFH)', 'Underground')], ['ue', L(t, 'Überflur (SPÜL-FLEX ÜFH)', 'Above ground')], ['garden', L(t, 'Gartenhydrant / Anschlussleitung', 'Garden hydrant / service line')]] },
    { k: 'vol', label: (t) => L(t, 'Spülmenge je Tag und Punkt (m³)', 'Flush volume per day and point (m³)'), def: 0.5 },
    { k: 'gsm', type: 'chk', label: (t) => L(t, 'Fernsteuerung GSM', 'GSM remote control'), def: false },
    { k: 'drain', type: 'chk', label: (t) => L(t, 'Spülwasser versickern (BEAULEX)', 'Infiltrate flush water (BEAULEX)'), def: true },
    { k: 'rv', type: 'chk', label: (t) => L(t, 'Rückflusssicherung KR-STOP', 'Backflow stop KR-STOP'), def: true },
    { k: 'box', type: 'chk', label: (t) => L(t, 'Hydranten-Spülbox', 'Hydrant flushing box'), def: false },
  ],
  calc(p) { const cnt = Math.max(1, Math.round(n(p.cnt, 1))), vol = n(p.vol, 0.5); return { cnt, dn: dnOf(p.dn), vol, year: cnt * vol * 365, rig: Math.max(0.5, Math.ceil(vol * 2) / 2), warn: [] }; },
  kpis: (t, c, f) => [[L(t, 'Spülpunkte', 'Points'), c.cnt, 'cnt'], ['DN', c.dn, 'dn'], [L(t, 'Spülwasser / Jahr', 'Flush water / year'), `${f(c.year)} m³`, 'year'], [L(t, 'Rigole je Punkt', 'Soakaway per point'), `${f(c.rig, 1)} m³`, 'rig']],
  bom(t, p, c, I, add) {
    if (p.kind === 'garden') { add(pick(I, 'flush-garden'), c.cnt); add(pick(I, 'flush-set'), c.cnt); } else add(pick(I, p.kind === 'ue' ? 'flush-ue' : 'flush-uf', { dn: c.dn }), c.cnt);
    if (p.gsm) add(pick(I, '72gsm'), c.cnt);
    if (p.kind === 'uf') add(pick(I, 'cap', { dim: 'Hydrant' }), c.cnt);
    add(pick(I, 'tjet', { dn: c.dn }), c.cnt);
    if (p.rv !== false) add(pick(I, 'krstop', { dim: 'Storz C' }), c.cnt);
    if (p.drain !== false) add(pick(I, 'beaulex'), c.cnt * c.rig, 'm3');
    if (p.box) add(pick(I, 'spuelbox'), 1);
  },
};

const HYD = {
  id: 'hydrant', icon: '🧯', name: (t) => L(t, 'Hydrant / Objektschutz DIN 14462', 'Hydrant / property protection'),
  fields: [
    { k: 'cnt', label: (t) => L(t, 'Anzahl Hydranten', 'Number of hydrants'), def: 2 },
    { k: 'dn', type: 'sel', label: (t) => L(t, 'Hydrant DN', 'Hydrant DN'), def: 100, opts: [[80, 'DN 80'], [100, 'DN 100'], [150, 'DN 150']] },
    { k: 'kind', type: 'sel', label: (t) => L(t, 'Bauart', 'Type'), def: 'uf', opts: (t) => [['uf', L(t, 'Unterflur', 'Underground')], ['ue', L(t, 'Überflur', 'Above ground')], ['combi', L(t, 'Kombi Hydrant + BEV', 'Combi hydrant + air valve')]] },
    { k: 'rd', type: 'sel', label: (t) => L(t, 'Rohrdeckung', 'Pipe cover'), def: '1,25', opts: [['1,00', '1,00 m'], ['1,25', '1,25 m'], ['1,50', '1,50 m']] },
    { k: 'dist', label: (t) => L(t, 'Leitungslänge Objekt (m)', 'Pipe length (m)'), def: 150 },
    { k: 'flush', type: 'chk', label: (t) => L(t, 'Mit Spülsystem 72plus', 'With 72plus flushing'), def: false },
    { k: 'meter', type: 'chk', label: (t) => L(t, 'Standrohr mit Wasserzähler', 'Standpipe with meter'), def: false },
  ],
  calc(p) { const cnt = Math.max(1, Math.round(n(p.cnt, 2))), dist = n(p.dist, 150); return { cnt, dn: n(p.dn, 100), dist, spacing: dist / cnt, warn: dist / cnt > 150 ? ['hyWarnDist'] : [] }; },
  kpis: (t, c, f) => [[L(t, 'Hydranten', 'Hydrants'), c.cnt, 'cnt'], ['DN', c.dn, 'dn'], [L(t, 'Abstand ca.', 'Spacing approx.'), `${f(c.spacing)} m`, 'spacing']],
  bom(t, p, c, I, add) {
    if (p.kind === 'combi') add(pick(I, 'combi', { dn: c.dn }), c.cnt); else add(pick(I, p.kind === 'ue' ? 'hyd-ue' : 'hyd-uf', { dn: c.dn, dim: `RD ${p.rd || '1,25'}` }), c.cnt);
    if (p.kind !== 'ue') add(pick(I, 'cap', { dim: 'Hydrant' }), c.cnt);
    add(pick(I, 'pas', { dn: c.dn, dim: 'PN 10/16' }), c.cnt);
    if (p.flush) add(pick(I, '72plus'), c.cnt);
    if (p.meter) add(pick(I, 'standrohr'), 1);
    add(pick(I, 'krstop', { dim: 'Storz B' }), c.cnt);
  },
};

const RAIN = {
  id: 'rain', icon: '🌧️', name: (t) => L(t, 'Regenwasserspeicher / Versickerung', 'Rainwater storage / infiltration'),
  fields: [
    { k: 'area', label: (t) => L(t, 'Angeschlossene Fläche (m²)', 'Connected area (m²)'), def: 500 },
    { k: 'psi', label: (t) => L(t, 'Abflussbeiwert (Dach 0,9 · Pflaster 0,7)', 'Runoff coefficient'), def: 0.9 },
    { k: 'mm', label: (t) => L(t, 'Bemessungsregen (mm)', 'Design rainfall (mm)'), def: 30 },
    { k: 'dn', type: 'sel', label: (t) => L(t, 'Zulauf DN', 'Inlet DN'), def: 150, opts: [[100, 'DN 100'], [150, 'DN 150'], [200, 'DN 200'], [300, 'DN 300']] },
    { k: 'filter', type: 'chk', label: (t) => L(t, 'Filterschacht (P-FIX)', 'Filter chamber (P-FIX)'), def: true },
    { k: 'back', type: 'chk', label: (t) => L(t, 'Rückstausicherung Überlauf', 'Overflow backflow stop'), def: true },
    { k: 'use', type: 'chk', label: (t) => L(t, 'Nutzung: Gartenhydrant', 'Use: garden hydrant'), def: false },
    { k: 'fire', type: 'chk', label: (t) => L(t, 'Zusätzlich Löschwasser-Saugstelle', 'Additional fire suction point'), def: false },
    { k: 'load', type: 'sel', label: (t) => t.fpLoad2, def: 'green', opts: (t) => [['road', t.fpLoadRoad], ['green', t.fpLoadGreen]] },
  ],
  calc(p) { const V = (n(p.area, 500) * n(p.psi, 0.9) * n(p.mm, 30)) / 1000; return { V, balls: Math.ceil((V / 0.9) * 10) / 10, dn: n(p.dn, 150), warn: [] }; },
  kpis: (t, c, f) => [[L(t, 'Speichervolumen', 'Storage volume'), `${f(c.V, 1)} m³`, 'v'], [L(t, 'BEAULEX-Kugeln', 'BEAULEX balls'), `${f(c.balls, 1)} m³`, 'balls'], [L(t, 'Zulauf', 'Inlet'), `DN ${c.dn}`, 'dn']],
  bom(t, p, c, I, add, other) {
    add(pick(I, 'beaulex'), c.balls, 'm3');
    other(L(t, 'Geotextil / Vlies (Rigolen-Umhüllung)', 'Geotextile wrap'), '', 'PP', 'm2', Math.ceil(6 * Math.pow(c.balls, 2 / 3)));
    if (p.filter !== false) { add(pick(I, 'filter'), 1); add(pick(I, 'shaft', { dn: 1000 }), 1); add(pick(I, 'cover', { cls: p.load === 'road' ? 'D400' : 'B125' }), 1); }
    if (p.back !== false) add(pick(I, 'backflow', { dn: c.dn }), 1);
    if (p.use) add(pick(I, 'flush-garden'), 1);
    if (p.fire) { add(pick(I, 'lw-saug-frost', { dn: 125 }), 1); add(pick(I, 'lw-korb', { dn: 125 }), 1); add(pick(I, 'lw-sign'), 1); }
  },
};

const BEV = {
  id: 'bev', icon: '💨', name: (t) => L(t, 'Be- und Entlüftung (BEV)', 'Air valve station'),
  fields: [
    { k: 'cnt', label: (t) => L(t, 'Anzahl Hochpunkte', 'Number of high points'), def: 1 },
    { k: 'dn', type: 'sel', label: (t) => L(t, 'Leitung DN', 'Main DN'), def: 100, opts: DNS.filter(([d]) => d >= 80) },
    { k: 'low', type: 'chk', label: (t) => L(t, 'Geringe Überdeckung (< 1,0 m)', 'Low cover (< 1.0 m)'), def: false },
    { k: 'flood', type: 'chk', label: (t) => L(t, 'Überflutungsgefährdet (Lüftungskappe)', 'Flood risk (vent cap)'), def: true },
  ],
  calc(p) { const dn = n(p.dn, 100); return { cnt: Math.max(1, Math.round(n(p.cnt, 1))), dn, bev: dn <= 100 ? 50 : 80, warn: [] }; },
  kpis: (t, c) => [[L(t, 'BEV-Garnituren', 'Air valves'), c.cnt, 'cnt'], [L(t, 'Leitung', 'Main'), `DN ${c.dn}`, 'dn'], ['BEV', `DN ${c.bev}`, 'bev']],
  bom(t, p, c, I, add) {
    add(p.low ? pick(I, 'bev-pe', { dn: c.bev }) : pick(I, 'bev', { dn: c.bev, name: c.dn >= 200 ? 'FLEX 700' : 'FLEX 400' }), c.cnt);
    add(pick(I, p.flood !== false ? 'cap-air' : 'cap', { dim: 'Schieber' }), c.cnt);
    add(pick(I, 'tee', { dn: c.dn }), c.cnt);
  },
};

const METER = {
  id: 'meter', icon: '📟', name: (t) => L(t, 'Wasserzähler- / Übergabeschacht', 'Meter / transfer chamber'),
  fields: [
    { k: 'dn', type: 'sel', label: (t) => L(t, 'Zähler DN', 'Meter DN'), def: 100, opts: DNS },
    { k: 'kind', type: 'sel', label: (t) => L(t, 'Schacht', 'Chamber'), def: 'kompact', opts: (t) => [['kompact', L(t, 'KOMPACT PEHD DN 1500', 'KOMPACT PE DN 1500')], ['vario', 'VARIO-FIX-BOX smart'], ['molch', L(t, 'FLEX Clean Molch-/WZ-Station', 'FLEX Clean pig / meter station')]] },
    { k: 'load', type: 'sel', label: (t) => t.fpLoad2, def: 'road', opts: (t) => [['road', t.fpLoadRoad], ['green', t.fpLoadGreen]] },
    { k: 'rv', type: 'chk', label: (t) => L(t, 'Rückschlagventil STOP SILENT', 'Check valve STOP SILENT'), def: true },
    { k: 'mon', type: 'chk', label: (t) => L(t, 'Überwachung VIDI CONTROLS', 'Monitoring VIDI CONTROLS'), def: false },
  ],
  calc(p) { return { dn: n(p.dn, 100), warn: [] }; },
  kpis: (t, c) => [[L(t, 'Zähler', 'Meter'), `DN ${c.dn}`, 'dn']],
  bom(t, p, c, I, add) {
    add(pick(I, 'meter', { dn: c.dn }), 1);
    if (p.kind === 'vario') add(pick(I, 'vario-smart'), 1); else if (p.kind === 'molch') add(pick(I, 'molch', { dn: c.dn }), 1); else add(pick(I, 'shaft', { dn: 1500 }), 1);
    add(pick(I, 'pas', { dn: c.dn, dim: 'PN 10/16' }), 2);
    if (p.rv !== false) add(pick(I, 'rv', { dn: c.dn }), 1);
    add(pick(I, 'cover', { cls: p.load === 'green' ? 'B125' : 'D400', dim: '1000x1000' }), 1);
    if (p.mon) add(pick(I, 'lw-level'), 1);
  },
};

const SEWER = {
  id: 'sewer', icon: '🛑', name: (t) => L(t, 'Rückstausicherung Abwasser / Regenwasser', 'Sewer backflow protection'),
  fields: [
    { k: 'cnt', label: (t) => L(t, 'Anzahl Anschlüsse', 'Number of connections'), def: 1 },
    { k: 'dn', type: 'sel', label: 'DN', def: 150, opts: [[100, 'DN 100'], [150, 'DN 150'], [200, 'DN 200'], [300, 'DN 300']] },
    { k: 'odor', type: 'chk', label: (t) => L(t, 'Geruchsverschluss AIR', 'Odour trap AIR'), def: true },
    { k: 'press', type: 'chk', label: (t) => L(t, 'Druckleitung: Spülhydrant', 'Pressure main: flushing hydrant'), def: false },
  ],
  calc(p) { return { cnt: Math.max(1, Math.round(n(p.cnt, 1))), dn: n(p.dn, 150), warn: [] }; },
  kpis: (t, c) => [[L(t, 'Anschlüsse', 'Connections'), c.cnt, 'cnt'], ['DN', c.dn, 'dn']],
  bom(t, p, c, I, add) {
    add(pick(I, 'backflow', { dn: c.dn }), c.cnt);
    if (p.odor !== false) add(pick(I, 'odor', { dn: c.dn }), c.cnt);
    if (p.press) add(pick(I, 'sew-flush', { dn: Math.min(c.dn, 150) }), 1);
  },
};

const FREE = { id: 'free', icon: '🧩', name: (t) => L(t, 'Freie Zusammenstellung', 'Free composition'), fields: [], calc: () => ({ warn: [] }), kpis: () => [], bom() {} };

export const PLANS = [POND, FLUSH, HYD, RAIN, BEV, METER, SEWER, FREE];
export const planDefaults = (pl) => Object.fromEntries(pl.fields.map((f) => [f.k, f.def]));

export function planBom(t, pl, p, doc, extra = []) {
  const I = doc?.items || [], out = [], c = pl.calc(p);
  const add = (it, qty, unit) => it && qty > 0 && out.push({ ref: it.id, mfrId: doc.id, mfr: doc.name, name: it.name, dim: it.dim, mat: it.mat, sku: it.sku || '', unit: unit || it.unit || 'pcs', qty: Math.round(qty * 100) / 100, price: it.price ?? null, url: it.url || '' });
  const other = (name, dim, mat, unit, qty) => out.push({ ref: `x-${name}-${dim}`, mfrId: '', mfr: '', name, dim, mat, sku: '', unit, qty, price: null, url: '', other: true });
  pl.bom(t, p, c, I, add, other);
  // merge equal items (e.g. same cap twice)
  const merged = [];
  [...out, ...extra].forEach((r) => { const m = merged.find((x) => x.ref === r.ref && x.unit === r.unit && !r.free); if (m) m.qty = Math.round((m.qty + r.qty) * 100) / 100; else merged.push({ ...r }); });
  return { c, rows: merged.map((r, i) => ({ ...r, pos: i + 1 })) };
}
